import axios from "axios"
import { BASE_URL } from "../../features/endpoints/endpointManager"
import { getFilesPath } from "./cmsConfig"

// Get CMS file path using centralized config
const getCMSFilePath = (): string => {
  return getFilesPath()
}

type ValidFileNames =
  | "termsOfUse"
  | "privacyPolicy"
  | "authAndRelease"
  | "knoxKeene"
  | "practiceAgreement"
  | "disclosures"

// Map camelCase to underscore naming convention used in CMS
export const fileNameMapping: Record<ValidFileNames, string> = {
  termsOfUse: "terms_of_use",
  privacyPolicy: "privacy_policy",
  authAndRelease: "auth_and_releases",
  knoxKeene: "knox_keene",
  practiceAgreement: "practice_agreement",
  disclosures: "disclosures",
}

// CMS file paths (base or live) - computed when file details are created/reinitialized
const getFileUrl = (
  fileName: ValidFileNames,
  extension: string = "",
): string => {
  const cmsFilePath = getCMSFilePath()
  const actualFileName = fileNameMapping[fileName] || fileName
  return `${cmsFilePath}/${actualFileName}${extension}`
}

// Helper function to create file details object
const createFileDetails = () => ({
  termsOfUse: {
    staticUrl: getFileUrl("termsOfUse", ".pdf"),
  },
  privacyPolicy: {
    staticUrl: getFileUrl("privacyPolicy", ".pdf"),
  },
  authAndRelease: {
    staticUrl: getFileUrl("authAndRelease", ".docx.b64"),
  },
  knoxKeene: {
    staticUrl: getFileUrl("knoxKeene", ".docx.b64"),
  },
  practiceAgreement: {
    staticUrl: getFileUrl("practiceAgreement", ".docx.b64"),
  },
  disclosures: {
    staticUrl: getFileUrl("disclosures", ".docx.b64"),
  },
})

// staticUrl: new static cms file approach
// Initially created with base paths, will be reinitialized after CMS mode is determined
// eslint-disable-next-line import/no-mutable-exports -- fileDetails needs to be mutable for CMS mode reinitialization
export let fileDetails = createFileDetails()

/**
 * Reinitialize file details after CMS mode has been determined
 * This ensures that staticUrl paths reflect the correct CMS mode (live vs base)
 */
export const reinitializeFileDetails = (): void => {
  fileDetails = createFileDetails()
}

// Static CMS doesn't need the ID, and file is already base64 encoded, just need to hand it back.
export const fetchB64DocxFile = async (
  staticUrl: string,
): Promise<string | null> => {
  try {
    // Static files are served from the base server URL without the /cms path
    const response = await axios.get(`${BASE_URL}${staticUrl}`, {
      responseType: "text",
    })
    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(
        `Failed to fetch CMS file ${staticUrl} with status code: ${response.status}`,
      )
    }
  } catch (error) {
    throw error
  }
}
